import { ACCOUNT_LEVEL_CAPABILITIES } from 'constants/capabilities'
import {
  BulkCapabilityRequest,
  BulkCapabilityRequestFilterObject,
  BulkCapabilityResponse,
  CapabilitiesScope,
} from 'types/capabilities/capabilities'

export const getWalletActionCapabilityRequestFilters = (walletLsid: string[]) => {
  if (!walletLsid?.length) return

  const walletFilters: BulkCapabilityRequestFilterObject[] = walletLsid?.map(walletLsid => {
    return {
      scope: CapabilitiesScope.WALLET,
      subjectId: walletLsid,
    }
  })

  return {
    capabilities: [
      {
        group: 'FIAT',
        name: 'WALLET_ACTIONS',
        objects: walletFilters,
      },
      {
        group: 'PAYMENT',
        name: 'INTERNAL',
        objects: walletFilters,
      },
    ],
  } as BulkCapabilityRequest
}

export const getAccountCapabilityRequestFilters = (accountReference: string) => {
  if (!accountReference) return

  const accountFilters: BulkCapabilityRequestFilterObject[] = [
    {
      scope: CapabilitiesScope.ACCOUNT,
      subjectId: accountReference,
    },
  ]

  const accountCapabilityFilters = ACCOUNT_LEVEL_CAPABILITIES?.map(accountCapability => {
    return {
      ...accountCapability,
      objects: accountFilters,
    }
  })

  return {
    capabilities: accountCapabilityFilters,
  } as BulkCapabilityRequest
}

/**
 * Custom hook to return account capabilities from the capabilities management service
 *
 * @param {string} accountReference - The account reference to fetch capabilities on an account level
 * @param {BulkCapabilityResponse} bulkCapabilities - The bulk wallet capabilities response.
 * @returns {AccountCapabilities} - Returns the account capabilities or null if not yet fetched.
 */
export const getAccountCapabilities = (
  accountReference: string,
  bulkCapabilities: BulkCapabilityResponse
) => {
  const isPrincipleCustomerCompanyCapabilityEnabled =
    bulkCapabilities?.ACCOUNT?.PRINCIPLE_CUSTOMER_COMPANY?.ACCOUNT?.[accountReference]?.value ===
    'true'

  const isPrincipleCustomerIndividualCapabilityEnabled =
    bulkCapabilities?.ACCOUNT?.PRINCIPLE_CUSTOMER_INDIVIDUAL?.ACCOUNT?.[accountReference]?.value ===
    'true'

  const isEmbeddedCustomerCompanyCapabilityEnabled =
    bulkCapabilities?.ACCOUNT?.EMBEDDED_CUSTOMER_COMPANY?.ACCOUNT?.[accountReference]?.value ===
    'true'

  const isEmbeddedCustomerIndividualCapabilityEnabled =
    bulkCapabilities?.ACCOUNT?.EMBEDDED_CUSTOMER_INDIVIDUAL?.ACCOUNT?.[accountReference]?.value ===
    'true'

  const isBvnkNetworkCapabilityEnabled =
    bulkCapabilities?.NETWORK?.PARTICIPANT?.ACCOUNT?.[accountReference]?.value === 'true'

  const isSwiftBeneficiaryCapabilityEnabled =
    bulkCapabilities?.FIAT?.BENEFICIARIES_SWIFT?.ACCOUNT?.[accountReference]?.value === 'true'

  const isB2CBeneficiaryCapabilityEnabled =
    bulkCapabilities?.FIAT?.BENEFICIARIES_B2C?.ACCOUNT?.[accountReference]?.value === 'true'

  const isCubixBeneficiaryCapabilityEnabled =
    bulkCapabilities?.FIAT?.BENEFICIARIES_CUBIX?.ACCOUNT?.[accountReference]?.value === 'true'

  return {
    isPrincipleCustomerCompanyCapabilityEnabled,
    isPrincipleCustomerIndividualCapabilityEnabled,
    isBvnkNetworkCapabilityEnabled,
    isSwiftBeneficiaryCapabilityEnabled,
    isB2CBeneficiaryCapabilityEnabled,
    isCubixBeneficiaryCapabilityEnabled,
    isEmbeddedCustomerCompanyCapabilityEnabled,
    isEmbeddedCustomerIndividualCapabilityEnabled,
  }
}
